import React, { useEffect, useState } from "react";
import { SlButton, SlInput, SlSelect, SlOption, SlDrawer } from "@shoelace-style/shoelace/dist/react";
import API from "../../services/api";
import Paginator from "../../shared/Paginator";
import { Toast } from "../../shared/Toast";
import { debounce } from "../../utils/Debounce";
import "./BirdViewDuplicates.scss";

const LIMIT = 20;

/**
 * Review queue for duplicate bird_view ads.
 *
 * Each card is one group (parent_id) - the same ad captured across several
 * editions. Approving merges the group onto the primary ad: the locations are
 * combined, the ad is re-priced against the combined-location page rate, and the
 * other bird_view rows are deleted. Rejecting just drops the group from the
 * queue. Either way the bird_view_duplicates rows are kept as the backup.
 */
function BirdViewDuplicates() {
  const [groups, setGroups] = useState([]);
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // filters
  const [search, setSearch] = useState("");
  const [adSource, setAdSource] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  // approve confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [acting, setActing] = useState(false);

  // image lightbox
  const [lightbox, setLightbox] = useState(null);

  const fetchGroups = async (page = 0) => {
    setLoader(true);
    try {
      const res = await API({
        url: "/birdviewduplicates/get",
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: LIMIT,
          search,
          adSource,
          dateRange: {
            startDate: dateRange.startDate || null,
            endDate: dateRange.endDate || null,
          },
        },
      });
      if (res.code === 200) {
        const tc = res.data?.totalCount || 0;
        setGroups(res.data?.res || []);
        setTotalCount(tc);
        setTotalPages(Math.ceil(tc / LIMIT));
      } else {
        setGroups([]);
        setTotalCount(0);
        setTotalPages(0);
        Toast("error", res?.message);
      }
    } catch (err) {
      console.error(err);
      Toast("error", err?.response?.data?.message || "Could not load duplicates");
    }
    setLoader(false);
  };

  useEffect(() => {
    setCurrentPage(0);
    fetchGroups(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, adSource, dateRange.startDate, dateRange.endDate]);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    fetchGroups(selected);
  };

  const onSearch = debounce((e) => setSearch(e.target.value), 500);

  /**
   * The figures are already on the card (the list computes them), so the
   * confirmation opens instantly. approve() recomputes server-side and fails
   * loudly if anything changed in the meantime.
   */
  const openConfirm = (group) => {
    setPreview({ group, merge: group.merge, error: group.merge_error });
    setConfirmOpen(true);
  };

  const approve = async () => {
    setActing(true);
    try {
      const res = await API({
        url: "/birdviewduplicates/approve",
        method: "POST",
        responseType: "json",
        data: { parent_id: preview.group.parent_id },
      });
      if (res.code === 200) {
        Toast("success", "Duplicates merged successfully");
        setConfirmOpen(false);
        setPreview(null);
        fetchGroups(currentPage);
      } else {
        Toast("error", res?.message || "Merge failed");
      }
    } catch (err) {
      console.error(err);
      Toast("error", err?.response?.data?.message || "Merge failed");
    }
    setActing(false);
  };

  const reject = async (group) => {
    setActing(true);
    try {
      const res = await API({
        url: "/birdviewduplicates/reject",
        method: "POST",
        responseType: "json",
        data: { parent_id: group.parent_id },
      });
      if (res.code === 200) {
        Toast("success", "Group removed from the queue");
        fetchGroups(currentPage);
      } else {
        Toast("error", res?.message || "Could not reject");
      }
    } catch (err) {
      console.error(err);
      Toast("error", err?.response?.data?.message || "Could not reject");
    }
    setActing(false);
  };

  const fmtDate = (unix) =>
    unix ? new Date(unix * 1000).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const dims = (ad) => {
    const d = ad?.dimensions;
    return d?.w && d?.h ? `${Math.round(d.w)}×${Math.round(d.h)}` : "—";
  };

  const sourceChip = (group) => {
    if (group.is_alpha) return <span className="bvd-chip bvd-chip--alpha">Alpha</span>;
    if (group.brand_id > 0)
      return <span className="bvd-chip bvd-chip--brand">{group.brand_name || `Brand ${group.brand_id}`}</span>;
    return <span className="bvd-chip bvd-chip--none">No brand</span>;
  };

  return (
    <div className="bvd">
      <div className="bvd-bar">
        <div className="bvd-title">
          Duplicate Ads
          <small>{totalCount} pending group{totalCount === 1 ? "" : "s"}</small>
        </div>

        <SlInput
          className="bvd-search"
          placeholder="Search campaign, edition, newspaper, brand"
          size="small"
          clearable
          onSlInput={onSearch}
        />

        <SlInput
          type="date"
          size="small"
          label="From"
          value={dateRange.startDate}
          onSlChange={(e) => setDateRange((p) => ({ ...p, startDate: e.target.value }))}
        />
        <SlInput
          type="date"
          size="small"
          label="To"
          value={dateRange.endDate}
          onSlChange={(e) => setDateRange((p) => ({ ...p, endDate: e.target.value }))}
        />

        <SlSelect
          size="small"
          value={adSource}
          className="bvd-source"
          onSlChange={(e) => setAdSource(e.target.value)}
        >
          <SlOption value="">All ads</SlOption>
          <SlOption value="brand">Brand ads</SlOption>
          <SlOption value="alpha">Alpha ads</SlOption>
        </SlSelect>
      </div>

      {loader && <div className="bvd-empty">Loading…</div>}

      {!loader && !groups.length && <div className="bvd-empty">No duplicate groups pending review.</div>}

      {!loader &&
        groups.map((group, idx) => (
          <section className="bvd-group" key={group.parent_id}>
            <div className="bvd-ghead">
              <span className="bvd-gidx">#{currentPage * LIMIT + idx + 1}</span>
              <span className="bvd-gpid">{group.parent_id}</span>
              <span className="bvd-gcamp">{group.campaign_name || "(no campaign)"}</span>
              <span className="bvd-gmeta">
                {fmtDate(group.date)} · {group.newspaper_name || "—"} · {group.ad_count} ads
              </span>
              {sourceChip(group)}
              <div className="bvd-actions">
                <SlButton size="small" variant="danger" outline disabled={acting} onClick={() => reject(group)}>
                  Reject
                </SlButton>
                <SlButton
                  size="small"
                  variant="primary"
                  disabled={acting || !group.merge}
                  title={group.merge_error || "Merge this group into the primary ad"}
                  onClick={() => openConfirm(group)}
                >
                  Approve &amp; Merge
                </SlButton>
              </div>
            </div>

            <div className="bvd-body">
            <div className="bvd-strip">
              {(group.ads || []).map((ad) => (
                <div className={`bvd-tile ${ad.is_primary ? "bvd-tile--primary" : ""}`} key={ad.id}>
                  {ad.is_primary && <span className="bvd-tag">PRIMARY</span>}
                  <div
                    className="bvd-imgwrap"
                    onClick={() => setLightbox(ad)}
                    title="Click to enlarge"
                  >
                    {ad.image_url ? (
                      <img loading="lazy" src={ad.image_url} alt={`ad ${ad.id}`} />
                    ) : (
                      <div className="bvd-imgfail">no image</div>
                    )}
                  </div>
                  <div className="bvd-meta">
                    <div className="bvd-row">
                      <span className="k">id</span>
                      <span className="v">{ad.id}</span>
                    </div>
                    <div className="bvd-row">
                      <span className="k">edition</span>
                      <span className="v">{ad.edition || "—"}</span>
                    </div>
                    <div className="bvd-row">
                      <span className="k">page</span>
                      <span className="v">{ad.page_no ?? "—"}</span>
                    </div>
                    <div className="bvd-row">
                      <span className="k">size</span>
                      <span className="v">{dims(ad)}</span>
                    </div>
                    <div className="bvd-row">
                      <span className="k">est. value</span>
                      <span className="v">{ad.estimated_value ?? "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* After-merge figures, so the numbers can be checked before acting */}
            <aside className="bvd-merge">
              {group.merge ? (
                <>
                  <div className="bvd-merge-head">After merge</div>
                  <div className="bvd-merge-body">
                    <div className="bvd-delta">
                      <span className="k">edition</span>
                      <span className="was">{group.merge.current_edition || "—"}</span>
                      <span className="now">{group.merge.edition}</span>
                    </div>
                    <div className="bvd-delta">
                      <span className="k">price / unit</span>
                      <span className="was">{group.merge.current_price_per_unit || "—"}</span>
                      <span className="now">{group.merge.price_per_unit}</span>
                    </div>
                    <div className="bvd-delta">
                      <span className="k">page rate id</span>
                      <span className="was">{group.merge.current_page_rate_id ?? "—"}</span>
                      <span className="now">{group.merge.page_rate_id}</span>
                    </div>
                    <div className="bvd-delta">
                      <span className="k">estimated value</span>
                      <span className="was">
                        {group.merge.current_total_value} <em>({group.ad_count} ads)</em>
                      </span>
                      <span className="now bvd-strong">{group.merge.estimated_value}</span>
                    </div>
                    <div className="bvd-row bvd-merge-foot">
                      <span className="k">change</span>
                      <span className={`v ${group.merge.value_delta < 0 ? "bvd-down" : "bvd-up"}`}>
                        {group.merge.value_delta > 0 ? "+" : ""}
                        {group.merge.value_delta}
                      </span>
                    </div>
                    <div className="bvd-row">
                      <span className="k">keeping</span>
                      <span className="v">{group.merge.primary_id}</span>
                    </div>
                    <div className="bvd-row">
                      <span className="k">deleting</span>
                      <span className="v">{group.merge.removing_ids.join(", ") || "—"}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bvd-merge-head bvd-merge-head--blocked">Cannot merge</div>
                  <div className="bvd-merge-body">
                    <p className="bvd-error">{group.merge_error}</p>
                  </div>
                </>
              )}
            </aside>
            </div>
          </section>
        ))}

      {!loader && totalPages > 1 && (
        <Paginator
          pageCount={totalPages}
          forcePage={currentPage}
          handlePageClick={handlePageClick}
          totalItems={totalCount}
          pageSize={LIMIT}
        />
      )}

      {/* Approve confirmation — shows exactly what will be written */}
      <SlDrawer
        label="Confirm merge"
        open={confirmOpen}
        onSlAfterHide={() => {
          setConfirmOpen(false);
          setPreview(null);
        }}
      >
        {preview?.error && <p className="bvd-error">{preview.error}</p>}

        {preview?.merge && (
          <div className="bvd-preview">
            <p>
              Ad <b>{preview.merge.primary_id}</b> will be updated and{" "}
              <b>{preview.merge.removing_ids.length}</b> duplicate ad
              {preview.merge.removing_ids.length === 1 ? "" : "s"} deleted from Bird View.
              This cannot be undone from here.
            </p>
            <div className="bvd-delta">
              <span className="k">edition</span>
              <span className="was">{preview.merge.current_edition || "—"}</span>
              <span className="now">{preview.merge.edition}</span>
            </div>
            <div className="bvd-delta">
              <span className="k">price / unit</span>
              <span className="was">{preview.merge.current_price_per_unit || "—"}</span>
              <span className="now">{preview.merge.price_per_unit}</span>
            </div>
            <div className="bvd-delta">
              <span className="k">page rate id</span>
              <span className="was">{preview.merge.current_page_rate_id ?? "—"}</span>
              <span className="now">{preview.merge.page_rate_id}</span>
            </div>
            <div className="bvd-delta">
              <span className="k">estimated value</span>
              <span className="was">{preview.merge.current_total_value}</span>
              <span className="now bvd-strong">{preview.merge.estimated_value}</span>
            </div>
            <div className="bvd-row">
              <span className="k">combined location</span>
              <span className="v">{preview.merge.combined_location}</span>
            </div>
            <div className="bvd-row">
              <span className="k">deleting ids</span>
              <span className="v">{preview.merge.removing_ids.join(", ") || "—"}</span>
            </div>
          </div>
        )}

        <div slot="footer">
          <SlButton variant="default" onClick={() => setConfirmOpen(false)}>
            Cancel
          </SlButton>
          <SlButton variant="primary" disabled={acting || !preview?.merge} onClick={approve}>
            {acting ? "Merging…" : "Confirm merge"}
          </SlButton>
        </div>
      </SlDrawer>

      {lightbox && (
        <div className="bvd-lb" onClick={() => setLightbox(null)}>
          <button className="bvd-lb-x" onClick={() => setLightbox(null)}>
            ×
          </button>
          <img src={lightbox.image_url} alt={`ad ${lightbox.id}`} onClick={(e) => e.stopPropagation()} />
          <div className="bvd-lb-cap">
            id {lightbox.id} ·{" "}
            <a href={lightbox.image_url} target="_blank" rel="noopener noreferrer">
              cropped
            </a>
            {lightbox.original_url && (
              <>
                {" "}·{" "}
                <a href={lightbox.original_url} target="_blank" rel="noopener noreferrer">
                  full page
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BirdViewDuplicates;
